import { Link } from '@tanstack/react-router';
import { Download, LogOut, Shield, Users } from 'lucide-react';
import { useExportTimeline } from '../../pages/case-room/use-export-timeline';
import type { TopBarCaseMenuActions } from './top-bar-menu-context';

interface TopBarProfileMenuPanelProps {
  readonly userEmail: string | null;
  readonly showAdminLink: boolean;
  readonly caseMenuActions: TopBarCaseMenuActions | null;
  readonly onClose: () => void;
  readonly onSignOut: () => void;
}

interface CaseActionsProps {
  readonly caseMenuActions: TopBarCaseMenuActions;
  readonly isExportPending: boolean;
  readonly onClose: () => void;
  readonly onExport: () => void;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  return 'Handlingen feilet.';
}

export function TopBarProfileMenuPanel({
  userEmail,
  showAdminLink,
  caseMenuActions,
  onClose,
  onSignOut,
}: TopBarProfileMenuPanelProps) {
  const exportTimeline = useExportTimeline();
  const exportError = errorMessage(exportTimeline.error);

  function handleExport() {
    if (!caseMenuActions || exportTimeline.isPending) return;
    onClose();
    exportTimeline.mutate({
      caseId: caseMenuActions.caseId,
      saksnummer: caseMenuActions.saksnummer,
      filter: caseMenuActions.filter,
    });
  }

  return (
    <div
      role="menu"
      className="absolute right-4 top-11 z-40 min-w-56 rounded-lg border border-white/10 bg-surface-raised p-1.5 shadow-2xl"
    >
      {userEmail && (
        <>
          <p className="px-2 pb-1.5 pt-1 text-[11px] text-text-tertiary">{userEmail}</p>
          <div className="mb-1 border-t border-white/10" />
        </>
      )}
      {showAdminLink && (
        <Link
          to="/admin"
          role="menuitem"
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary"
          onClick={onClose}
        >
          <Shield size={13} />
          Admin
        </Link>
      )}
      {caseMenuActions &&
        renderCaseActions({
          caseMenuActions,
          isExportPending: exportTimeline.isPending,
          onClose,
          onExport: handleExport,
        })}
      <div className="my-1 border-t border-white/10" />
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary"
        onClick={() => {
          onClose();
          onSignOut();
        }}
      >
        <LogOut size={13} />
        Logg ut
      </button>
      {exportError && caseMenuActions && (
        <p className="px-2 pt-1 text-[10px] text-red-300">{exportError}</p>
      )}
    </div>
  );
}

function renderCaseActions({
  caseMenuActions,
  isExportPending,
  onClose,
  onExport,
}: CaseActionsProps) {
  return (
    <>
      <button
        type="button"
        role="menuitem"
        disabled={isExportPending}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary disabled:opacity-50"
        onClick={onExport}
      >
        <Download size={13} />
        {isExportPending ? 'Eksporterer…' : 'Eksporter'}
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary"
        onClick={() => {
          onClose();
          caseMenuActions.onOpenMembers();
        }}
      >
        <Users size={13} />
        Medlemmer
      </button>
    </>
  );
}
